// The slide-over an 'open' action button with `presentation: 'drawer'` opens
// ANOTHER object in, above the screen the button was clicked on.
//
// One shell for every host (the platform Run view and designer preview, the
// downloaded runtime): the host decides WHAT goes inside — it is the one that
// knows how to draw a process, a dashboard or a visual — and calls `onClose`
// when the person is done, at which point the screen that opened it refreshes
// (the target may have saved something the source list shows).
//
// A transaction target opens its record in the ordinary record editor window,
// which is portalled above everything; see Z_OPEN_DRAWER for why the drawer
// sits just below that window and closes with it. A VIEW-mode transaction
// target is the exception: its record is drawn inside the drawer itself (the
// "details in a side panel" of a Vision link), so nothing of it opens above
// the drawer and the drawer clears the editor it was opened from.

import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { Z_OPEN_DRAWER, Z_OPEN_DRAWER_OVER_EDITOR } from '../../layers'
import { ShellEditorProvider } from '../../shell/nav/editorContext'

export interface ObjectDrawerProps {
  title: ReactNode
  /** The object type inside — decides the layer (see layers.ts). */
  objType: string
  /** The open button's mode for a transaction target. 'view' draws the record
   *  inside the drawer, so the drawer takes the over-editor layer. */
  openMode?: string
  onClose: () => void
  children: ReactNode
  /** Width in px (capped at the viewport). */
  width?: number
  /** The dialog's test hook (`data-tw-dialog`, see testHooks). */
  twDialog?: string
}

export default function ObjectDrawer({ title, objType, openMode, onClose, children, width = 1100, twDialog }: ObjectDrawerProps) {
  // No Escape-to-close: the windows opened inside the drawer (the record
  // editor, a picker) listen for Escape on the same window, and closing the
  // drawer underneath an editor that is asking about unsaved changes would
  // lose them. The ✕ and the backdrop close it.
  if (typeof document === 'undefined') return null
  const zIndex = objType === 'T' && openMode !== 'view' ? Z_OPEN_DRAWER : Z_OPEN_DRAWER_OVER_EDITOR
  return createPortal(
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex, display: 'flex', justifyContent: 'flex-end',
      background: 'rgba(15,23,42,0.35)',
    }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
        {...(twDialog ? { 'data-tw-dialog': twDialog } : {})}
        style={{
          width: `min(${width}px, 100%)`, height: '100%', display: 'flex', flexDirection: 'column',
          background: 'var(--color-bg, #fff)', color: 'var(--color-text, #1c2430)',
          borderLeft: '1px solid var(--color-border, #dde3ea)',
          boxShadow: '-12px 0 32px rgba(15,23,42,0.18)',
        }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
          borderBottom: '1px solid var(--color-border, #dde3ea)',
        }}>
          <strong style={{ fontSize: '1.0125rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </strong>
          <button type="button" onClick={onClose} title="Close" aria-label="Close"
            {...(twDialog ? { 'data-tw-action': 'close' } : {})}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 30, height: 30, borderRadius: 8, cursor: 'pointer',
              border: '1px solid var(--color-border, #dde3ea)', background: 'transparent',
              color: 'var(--color-text-secondary, #465062)',
            }}>
            <X size={16} />
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {/* A screen in a drawer is not the Run shell's screen: its editor opens
              in place, as it always has (never an Open-editors session). */}
          <ShellEditorProvider value={null}>{children}</ShellEditorProvider>
        </div>
      </div>
    </div>,
    document.body,
  )
}
